import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Cell, ReferenceLine,
} from "recharts";
import { ChartContainer } from "@/components/charts/ChartContainer";
import { formatNumber, formatDate } from "@/utils/format";
import type { InstagramNewFollowersDayPoint } from "@/services/marketing";
import { TOOLTIP_CURSOR_BAND } from "@/utils/rechartsInteraction";

interface Props {
  data: InstagramNewFollowersDayPoint[];
  loading?: boolean;
}

function formatChartDate(iso: string): string {
  const raw = (iso ?? "").trim().split("T")[0] ?? "";
  const parts = raw.split("-");
  if (parts.length < 3) return raw;
  return `${parts[2]}/${parts[1]}`;
}

function NewFollowersTooltip({
  active,
  payload,
}: {
  active?: boolean;
  payload?: Array<{ payload?: InstagramNewFollowersDayPoint & { label?: string } }>;
}) {
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload;
  if (!row) return null;
  return (
    <div className="min-w-[180px] rounded-xl border border-slate-200 bg-white p-3 shadow-modal">
      <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
        {formatDate(row.date)}
      </p>
      <p className="text-sm text-slate-700">
        Variação:{" "}
        <span className="font-semibold">
          {row.newFollowers >= 0 ? "+" : ""}
          {formatNumber(row.newFollowers)}
        </span>
      </p>
    </div>
  );
}

export function InstagramNewFollowersByDayChart({ data, loading }: Props) {
  const chartData = data.map((row) => ({
    ...row,
    label: formatChartDate(row.date),
  }));

  return (
    <ChartContainer
      title="Seguidores líquidos por dia"
      subtitle="Variação líquida do dia (seguiu − deixou de seguir), igual ao Insights"
      loading={loading}
      empty={!loading && chartData.length === 0}
      emptyMessage="Sem variação de seguidores no período."
    >
      <BarChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
        <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#64748b" }} />
        <YAxis tick={{ fontSize: 11, fill: "#64748b" }} width={48} />
        <Tooltip content={<NewFollowersTooltip />} cursor={TOOLTIP_CURSOR_BAND} />
        <ReferenceLine y={0} stroke="#94a3b8" />
        <Bar dataKey="newFollowers" radius={[3, 3, 0, 0]}>
          {chartData.map((entry) => (
            <Cell
              key={entry.date}
              fill={entry.newFollowers >= 0 ? "#10b981" : "#ef4444"}
            />
          ))}
        </Bar>
      </BarChart>
    </ChartContainer>
  );
}
